import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PoEditLine } from '@/api/onderaannemersPoEdit';
import type { TierBand } from '@/api/onderaannemers';
import { projectDashboardPath } from '@/utils/projectDashboardLink';

const euro = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' });
const m2fmt = new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 });

function parseDecimal(raw: string): number | null {
  const normalized = raw.replace(',', '.').trim();
  if (normalized === '') return null;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

export interface LineSavePatch {
  m2?: number;
  rate?: number;
  tier?: TierBand;
  label?: string;
}

interface PoEditLineRowProps {
  line: PoEditLine;
  // A section line can offer the under200⇄200+ tier switch only when we know the vendor
  // prices both tiers for its product. The parent computes this per row.
  tierSwitchable?: boolean;
  saving: boolean;
  onSave: (patch: LineSavePatch) => Promise<void>;
  onDelete: () => void;
}

/**
 * One sortable PO line. Sections render as a bold header band; product lines show
 * label · m² · rate · subtotal with an analytic chip, and inline ✎ edit / 🗑 delete.
 * Locked `[portal:*]` rows are greyed, non-draggable and non-editable.
 */
export function PoEditLineRow({ line, tierSwitchable, saving, onSave, onDelete }: PoEditLineRowProps) {
  const navigate = useNavigate();
  const isSection = line.displayType === 'line_section';
  const isNote = line.displayType === 'line_note';
  const goToDashboard = line.projectId != null
    ? () => navigate(projectDashboardPath(line.projectId!, line.collectorId))
    : null;
  const draggable = !line.locked;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: line.id, disabled: !draggable || saving });

  const [editing, setEditing] = useState(false);
  const [m2Draft, setM2Draft] = useState('');
  const [rateDraft, setRateDraft] = useState('');
  const [labelDraft, setLabelDraft] = useState('');
  const [tierDraft, setTierDraft] = useState<TierBand | ''>('');
  const [editError, setEditError] = useState<string | null>(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const startEdit = () => {
    setM2Draft(line.m2 != null ? String(line.m2).replace('.', ',') : '');
    setRateDraft(line.rate != null ? String(line.rate).replace('.', ',') : '');
    setLabelDraft(line.rawName);
    setTierDraft('');
    setEditError(null);
    setEditing(true);
  };

  const commit = async () => {
    const patch: LineSavePatch = {};
    if (isSection) {
      const trimmed = labelDraft.trim();
      if (trimmed && trimmed !== line.rawName) patch.label = trimmed;
    } else {
      const m2 = parseDecimal(m2Draft);
      if (m2 != null && m2 !== line.m2) patch.m2 = m2;
      const rate = parseDecimal(rateDraft);
      if (rate != null && rate !== line.rate) patch.rate = rate;
      if (tierDraft) patch.tier = tierDraft;
    }
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setEditError(null);
    try {
      await onSave(patch);
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Opslaan mislukt');
    }
  };

  // ── Section header ──────────────────────────────────────────────────────────
  if (isSection) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center gap-2 px-2 py-1.5 mt-2 first:mt-0 rounded ${
          line.locked ? 'bg-gray-100 text-gray-400 italic' : 'bg-td-blue/5'
        }`}
      >
        {draggable && (
          <button
            type="button"
            aria-label="Sleep sectie"
            className="shrink-0 cursor-grab active:cursor-grabbing text-td-muted touch-none"
            {...attributes}
            {...listeners}
          >
            ⠿
          </button>
        )}
        {editing ? (
          <input
            type="text"
            autoFocus
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void commit(); }
              else if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
            }}
            aria-label="Sectie titel"
            className="flex-1 min-w-0 text-xs font-semibold text-td-navy px-2 py-1 rounded border border-td-blue focus:outline-none focus:ring-2 focus:ring-td-blue/40"
          />
        ) : goToDashboard ? (
          <button
            type="button"
            onClick={goToDashboard}
            title="Open het dashboard van dit project"
            className="flex-1 min-w-0 text-left text-xs font-semibold text-td-navy hover:text-td-blue truncate cursor-pointer"
          >
            {line.label}
          </button>
        ) : (
          <span className="flex-1 min-w-0 text-xs font-semibold text-td-navy truncate">
            {line.label}
          </span>
        )}
        {!line.locked && !editing && (
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" onClick={startEdit} aria-label="Sectie hernoemen" title="Hernoemen"
              className="text-td-muted hover:text-td-blue text-sm">✎</button>
            <button type="button" onClick={onDelete} aria-label="Sectie verwijderen" title="Verwijderen"
              className="text-td-muted hover:text-red-600 text-sm">🗑</button>
          </div>
        )}
        {editError && <span className="text-[11px] text-red-600 shrink-0">{editError}</span>}
      </div>
    );
  }

  // ── Note / product line ─────────────────────────────────────────────────────
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-2 px-2 py-1.5 border-b border-gray-50 last:border-0 ${
        line.locked ? 'text-gray-400 italic' : ''
      }`}
    >
      {draggable ? (
        <button
          type="button"
          aria-label="Sleep regel"
          className="shrink-0 cursor-grab active:cursor-grabbing text-td-muted touch-none pt-0.5"
          {...attributes}
          {...listeners}
        >
          ⠿
        </button>
      ) : (
        <span className="shrink-0 w-[1ch] text-gray-300 pt-0.5" aria-hidden>·</span>
      )}

      <div className="min-w-0 flex-1">
        <div className={`text-sm ${isNote ? 'text-td-muted italic' : 'text-td-navy'}`}>{line.label}</div>
        {line.analytic && !isNote && (
          goToDashboard ? (
            <button
              type="button"
              onClick={goToDashboard}
              title="Open het dashboard van dit project"
              className="inline-block mt-0.5 text-[10px] font-medium text-td-muted hover:text-td-blue bg-gray-100 hover:bg-td-blue/10 px-1.5 py-0.5 rounded cursor-pointer"
            >
              {line.analytic.name}
            </button>
          ) : (
            <span className="inline-block mt-0.5 text-[10px] font-medium text-td-muted bg-gray-100 px-1.5 py-0.5 rounded">
              {line.analytic.name}
            </span>
          )
        )}
        {editing && !isNote && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-td-muted">
              m²
              <input
                type="text"
                inputMode="decimal"
                value={m2Draft}
                onChange={(e) => setM2Draft(e.target.value)}
                aria-label="Aantal m²"
                className="w-20 px-2 py-1 text-sm text-right rounded border border-gray-300 focus:border-td-blue focus:outline-none"
              />
            </label>
            <label className="flex items-center gap-1 text-xs text-td-muted">
              €
              <input
                type="text"
                inputMode="decimal"
                value={rateDraft}
                onChange={(e) => setRateDraft(e.target.value)}
                aria-label="Prijs per m²"
                className="w-24 px-2 py-1 text-sm text-right rounded border border-gray-300 focus:border-td-blue focus:outline-none"
              />
            </label>
            {tierSwitchable && (
              <label className="flex items-center gap-1 text-xs text-td-muted">
                Staffel
                <select
                  value={tierDraft}
                  onChange={(e) => setTierDraft(e.target.value as TierBand | '')}
                  aria-label="Staffel wisselen"
                  className="text-sm px-2 py-1 rounded border border-gray-300 bg-white focus:border-td-blue focus:outline-none"
                >
                  <option value="">— behoud —</option>
                  <option value="under200">Basistarief (&lt; 200 m²)</option>
                  <option value="from200">200+ tarief</option>
                </select>
              </label>
            )}
            <button type="button" onClick={commit} disabled={saving}
              className="text-xs font-semibold text-white bg-td-blue px-3 py-1 rounded disabled:opacity-50">
              {saving ? 'Bezig…' : 'Opslaan'}
            </button>
            <button type="button" onClick={() => setEditing(false)}
              className="text-xs font-semibold text-td-muted px-2 py-1 rounded hover:bg-gray-100">
              Annuleren
            </button>
            {editError && <span className="text-[11px] text-red-600">{editError}</span>}
          </div>
        )}
      </div>

      {!isNote && (
        <div className="shrink-0 text-right text-sm tabular-nums">
          <div className="text-td-muted">{line.m2 != null ? m2fmt.format(line.m2) : ''}</div>
        </div>
      )}
      {!isNote && (
        <div className="shrink-0 text-right text-sm tabular-nums w-20">
          <div className="text-td-muted">{line.rate != null ? euro.format(line.rate) : ''}</div>
        </div>
      )}
      {!isNote && (
        <div className="shrink-0 text-right text-sm tabular-nums w-24">
          <div className="font-medium text-td-navy">{line.subtotal != null ? euro.format(line.subtotal) : ''}</div>
        </div>
      )}

      {!line.locked && !isNote && !editing && (
        <div className="shrink-0 flex items-center gap-1 pt-0.5">
          <button type="button" onClick={startEdit} aria-label="Regel bewerken" title="Bewerken"
            className="text-td-muted hover:text-td-blue text-sm">✎</button>
          <button type="button" onClick={onDelete} aria-label="Regel verwijderen" title="Verwijderen"
            className="text-td-muted hover:text-red-600 text-sm">🗑</button>
        </div>
      )}
    </div>
  );
}
